import React, { useEffect, useRef, useState } from "react";
import API from "../lib/TimeEditAPI";
import Language from "../lib/Language";
import Log from "../lib/Log";
import { TSupportContact } from "../types/api/shared";

const TARGET = "_blank";

// supportURL / supportContacts / supportInfoLines were declared as props *and*
// held in state. No caller ever passed them -- withAppContext's `props: any`
// hid that. They are state only.
type THelpProps = {
    hasAvailableUpdate: boolean;
    resetHelp: () => void;
    showAbout: (supportInfoLines?: string[], showSupportText?: boolean) => void;
    showShortcuts: React.MouseEventHandler;
};

type THelpState = {
    supportURL: string;
    supportContacts: TSupportContact[];
    supportInfoLines: string[];
};

const Help = (props: THelpProps) => {
    const [state, setState] = useState<THelpState>({
        supportURL: "",
        supportInfoLines: [],
        supportContacts: [],
    });

    // The class guarded its async setState behind an _isMounted flag; a ref keeps
    // that guard rather than relying on React 18 quietly dropping the update.
    const isMounted = useRef(false);
    useEffect(() => {
        isMounted.current = true;
        API.getSupportInfo((supportURL, supportInfoLines, supportContacts) => {
            if (isMounted.current) {
                setState({
                    supportURL: supportURL || "",
                    supportInfoLines,
                    supportContacts: supportContacts || [],
                });
            }
        });
        return () => {
            isMounted.current = false;
        };
    }, []);

    const resetHelp = (event: React.MouseEvent) => {
        API.setPreferences("viewedHelp", ["{}"], () => {
            Log.info(Language.get("nc_client_side_user_menu_help_reset"));
            props.resetHelp();
        });
        event.preventDefault();
    };

    // The class picked this URL with `isNordic ? A : B` where A and B were the
    // same string, so the language lookup (and the only use of appContext here)
    // computed nothing. Collapsed to the single URL both branches produced.
    const createIssue = () => {
        window.open("https://www.timeedit.com/learn", TARGET);
    };

    const renderUpdateInfo = () => {
        if (!props.hasAvailableUpdate) {
            return null;
        }

        const style = {
            backgroundColor: "rgb(0, 103, 109)",
            padding: "5px",
            border: "1px solid #111",
        };

        return (
            <div style={style}>
                <strong>{Language.get("nc_timeedit_update_available")}</strong>
                <br />
                {Language.get("nc_timeedit_reload_to_get_latest_version")}
            </div>
        );
    };

    const renderSupportContacts = () => {
        if (state.supportContacts.length === 0) {
            return null;
        }

        const renderLine = (label: string | null, value: string, link?: string) => {
            if (!value) {
                return null;
            }
            if (link) {
                return (
                    <li>
                        {label ? <b>{label}: </b> : null}
                        <a href={link} target={TARGET}>
                            {value}
                        </a>
                    </li>
                );
            }
            return (
                <li>
                    <b>{label}:</b> {value}
                </li>
            );
        };

        const style = {
            padding: "5px",
            paddingBottom: "10px",
            borderBottom: "1px solid #eee",
            marginBottom: "10px",
        };

        return (
            <div>
                {state.supportContacts.map((contact, index) => (
                    <div key={index} style={style}>
                        <p style={{ marginTop: "0" }}>{contact.description}</p>
                        <ul>
                            {renderLine(Language.get("support_window_name"), contact.name)}
                            {renderLine(
                                Language.get("support_window_email"),
                                contact.email,
                                `mailto:${contact.email}`
                            )}
                            {renderLine(
                                Language.get("support_window_phone"),
                                contact.phone,
                                `tel://${contact.phone}`
                            )}
                            {renderLine(null, contact.url_title || contact.url, contact.url)}
                            {renderLine(
                                null,
                                contact.manual_url_title || contact.manual_url,
                                contact.manual_url
                            )}
                        </ul>
                    </div>
                ))}
            </div>
        );
    };

    const reportIssue = (
        <div>
            <div className="btnGroup vertical">
                <button className="deprecated button save" onClick={createIssue}>
                    {Language.get("nc_help_center")}
                </button>
            </div>
        </div>
    );

    return (
        <div className="helpPane">
            <h2>{Language.get("menu_help")}</h2>

            {renderUpdateInfo()}

            {renderSupportContacts()}

            {reportIssue}

            <div className="btnGroup vertical">
                <button className="deprecated save" onClick={props.showShortcuts}>
                    {Language.get("nc_keyboard_shortcuts")}
                </button>
            </div>
            <div className="btnGroup vertical">
                <button
                    className="deprecated save"
                    onClick={() => props.showAbout(state.supportInfoLines)}
                >
                    {Language.get("about_window_title")}
                </button>
            </div>

            <div className="btnGroup vertical">
                <button className="deprecated save" onClick={resetHelp}>
                    {Language.get("nc_menu_reset_help")}
                </button>
            </div>
        </div>
    );
};

export default Help;
